/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import utils from '../../../../common/utils';

const REQUEST_TIMEOUT_MESSAGE = 'Request timeout. Please try again.';

/**
 * Reads the reason out of the three shapes these routes return: `{ success: false, error }` from
 * the graph routes, `{ message }` from the report routes, and Joi's `{ error, message }`, where
 * `error` is only the status phrase. `message` therefore wins wherever both are present.
 */
export const resolveServerErrorMessage = (data) => {
  const { error, message } = data ?? {};
  if (typeof message === 'string' && message.trim()) return message.trim();
  return typeof error === 'string' ? error.trim() : '';
};

const isRequestTimeout = (error) =>
  error?.code === 'ECONNABORTED' || /^timeout of \d+ms exceeded$/.test(error?.message ?? '');

const notifyError = ({ message = '', langMessage = '' }) =>
  Framework.getStoreRegistry().getStore('notificationState').set({
    open: true,
    type: 'error',
    message,
    langMessage
  });

export default function handleEvalReportError(error, fallbackLangMessage) {
  const { response } = error ?? {};

  if (response?.status === 401) {
    utils.redirectToLogin();
    return;
  }

  const message = response
    ? resolveServerErrorMessage(response.data)
    : (isRequestTimeout(error) && REQUEST_TIMEOUT_MESSAGE) || '';

  if (message) {
    notifyError({ message });
    return;
  }
  if (fallbackLangMessage) {
    notifyError({ langMessage: fallbackLangMessage });
    return;
  }
  // Nothing usable came back, so fall back to whatever the client reported rather than
  // raising an empty toast.
  notifyError({ message: error?.message ?? '' });
}
